import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Typography,
  List,
  ListItem,
  IconButton,
  ListItemText,
  Alert,
  Box,
  Divider,
  Grid,
  Chip,
  Collapse,
  Snackbar,
  useMediaQuery,
  AppBar,
  Toolbar,
  Stack,
  Paper,
  InputLabel
} from "@mui/material";
import { Switch, FormControlLabel } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import DoDisturbIcon from "@mui/icons-material/DoDisturb";
import CloseIcon from "@mui/icons-material/Close";

import {
  doc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  getDoc,
  runTransaction,
  deleteField
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import { LEVELS, SUBJECTS } from "../constants";
import { handlePrint } from "../components/PrintReceipt";
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';


const ModifyStudentModal = ({ open, onClose, student, groups, teachers, setStudents, setGroups }) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery("(max-width:900px)");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [studentData, setStudentData] = useState({ ...student });
  const [selectedLevel, setSelectedLevel] = useState("");
  const [filteredSubjects, setFilteredSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [filteredTeachers, setFilteredTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState("");
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [showAddGroupInputs, setShowAddGroupInputs] = useState(false);
  const [originalStudentData, setOriginalStudentData] = useState(null);
  const [devicesDoc, setDevicesDoc] = useState(null); // { devices:[], models:{}, lastSeen:{} }
  const [loadingDevices, setLoadingDevices] = useState(false);

  const resetGroupSelections = () => {
    setSelectedTeacher("");
    setFilteredTeachers([]);
    setFilteredGroups([]);
    setSelectedGroup("");
  };

  useEffect(() => {
    if (student) {
      const initialData = {
        ...student,
        disabled: student.disabled !== undefined ? student.disabled : false
      };
      setStudentData(initialData);
      setOriginalStudentData(initialData);
      setSelectedLevel("");
      resetFilters();
    }
  }, [student]);


  const isModified = () => {
    if (!originalStudentData) return false;

    const fieldsChanged =
      (studentData.firstName || '') !== (originalStudentData.firstName || '') ||
      (studentData.lastName  || '') !== (originalStudentData.lastName  || '') ||
      (studentData.startDate || '') !== (originalStudentData.startDate || '') ||
      (studentData.endDate   || '') !== (originalStudentData.endDate   || '') ||
      (studentData.disabled  || false) !== (originalStudentData.disabled || false);

    const enrollmentsChanged =
      JSON.stringify(studentData.enrollments || []) !== JSON.stringify(originalStudentData.enrollments || []);

    return fieldsChanged || enrollmentsChanged;
  };


  // Reset filters
  const resetFilters = () => {
    setFilteredSubjects([]);
    setSelectedSubject("");
    setFilteredTeachers([]);
    setSelectedTeacher("");
    setFilteredGroups([]);
    setSelectedGroup("");
  };

  // Filter subjects by level
  useEffect(() => {
    if (selectedLevel) {
      const filtered = SUBJECTS.filter((subject) => subject.level === selectedLevel);
      setFilteredSubjects(filtered);
    } else {
      setFilteredSubjects([]);
    }
    setSelectedSubject("");
    setFilteredTeachers([]);
    setSelectedTeacher("");
    setFilteredGroups([]);
  }, [selectedLevel]);

  // Filter teachers by subject
  useEffect(() => {
    if (selectedSubject) {
      const filtered = teachers.filter((teacher) => teacher.subjectIds.includes(selectedSubject));
      setFilteredTeachers(filtered);
    } else {
      setFilteredTeachers([]);
    }
    setSelectedTeacher("");
    setFilteredGroups([]);
  }, [selectedSubject, teachers]);

  // Filter groups by teacher/subject/level
  useEffect(() => {
    if (selectedTeacher && selectedSubject && selectedLevel) {
      const filtered = groups.filter(
        (group) =>
          group.teacherId === selectedTeacher &&
          group.subjectId === selectedSubject &&
          group.level === selectedLevel
      );
      setFilteredGroups(filtered);
    } else {
      setFilteredGroups([]);
    }
    setSelectedGroup("");
  }, [selectedTeacher, selectedSubject, selectedLevel, groups]);

  // ---- Load devices for this student (Devices/{uid}) ----
  useEffect(() => {
    const uid = studentData?.uid;
    if (!open || !uid) {
      setDevicesDoc(null);
      return;
    }
    (async () => {
      try {
        setLoadingDevices(true);
        const ref = doc(db, "Devices", uid);
        const snap = await getDoc(ref);
        setDevicesDoc(snap.exists() ? snap.data() : { devices: [], models: {}, lastSeen: {} });
      } catch (e) {
        console.error("Failed to load devices:", e);
        setDevicesDoc({ devices: [], models: {}, lastSeen: {} });
      } finally {
        setLoadingDevices(false);
      }
    })();
  }, [open, studentData?.uid]);

  const formatDeviceLabel = (id) => {
    const model = devicesDoc?.models?.[id] || t("Unknown device");
    const short = id?.slice(0, 6);
    return `${model} — ${short}`;
  };

  const handleRemoveDevice = async (deviceId) => {
    if (!studentData?.uid) return;
    const ok = window.confirm(t("Do you want to remove this device from the student's account?"));
    if (!ok) return;

    try {
      const ref = doc(db, "Devices", studentData.uid);
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref);
        if (!snap.exists()) return; // nothing to do
        const data = snap.data() || {};
        const nextDevices = (data.devices || []).filter((d) => d !== deviceId);

        const update = { devices: nextDevices };
        // Remove keys from maps
        update[`models.${deviceId}`] = deleteField();
        update[`lastSeen.${deviceId}`] = deleteField();

        tx.update(ref, update);
      });

      // Update UI
      setDevicesDoc((prev) => {
        if (!prev) return prev;
        const next = {
          ...prev,
          devices: (prev.devices || []).filter((d) => d !== deviceId),
          models: { ...(prev.models || {}) },
          lastSeen: { ...(prev.lastSeen || {}) }
        };
        delete next.models?.[deviceId];
        delete next.lastSeen?.[deviceId];
        return next;
      });

      setSuccess(t("Device removed successfully"));
    } catch (e) {
      console.error("Failed to remove device:", e);
      setError(t("Failed to remove device"));
    }
  };

  // Add group
  const handleAddGroup = () => {
    if (!selectedGroup) {
      setError(t("Please select a group"));
      return;
    }
    const groupDetails = filteredGroups.find((group) => group.id === selectedGroup);
    if (!groupDetails) {
      setError(t("Invalid group"));
      return;
    }
    const isAlreadyEnrolled = studentData.enrollments.some(
      (enrollment) => enrollment.groupId === selectedGroup
    );
    if (isAlreadyEnrolled) {
      setError(t("Student is already enrolled in this group"));
      return;
    }
    setStudentData((prev) => ({
      ...prev,
      enrollments: [
        ...prev.enrollments,
        {
          groupId: groupDetails.id,
          groupName: groupDetails.name,
          subjectId: selectedSubject,
          teacherId: selectedTeacher,
          entryDate: new Date().toISOString()
        }
      ]
    }));
    setSuccess(t("Group added locally"));
    resetGroupSelections();
  };

  // Remove group
  const handleRemoveGroup = async (groupId) => {
    const confirm = window.confirm(t("Are you sure you want to remove this enrollment?"));
    if (!confirm) return;

    try {
      const enrollmentToRemove = studentData.enrollments.find(
        (enrollment) => enrollment.groupId === groupId
      );
      if (!enrollmentToRemove) {
        setError(t("Enrollment not found"));
        return;
      }

      await updateDoc(doc(db, "students", student.id), {
        enrollments: studentData.enrollments,
        firstName: studentData.firstName,
        lastName: studentData.lastName,
        phone: studentData.phone,
        disabled: studentData.disabled,
        startDate: studentData.startDate || null,   // <-- added
        endDate: studentData.endDate || null        // <-- added
      });

      await updateDoc(doc(db, "groups", groupId), {
        studentIds: arrayRemove(student.id)
      });

      setStudentData((prev) => ({
        ...prev,
        enrollments: prev.enrollments.filter((enrollment) => enrollment.groupId !== groupId)
      }));

      setSuccess(t("Enrollment removed successfully"));
    } catch (error) {
      console.error("Erreur lors de la suppression :", error);
      setError(t("An error occurred while deleting"));
    }
  };

  // Save
  const handleSave = async () => {
    try {
      await updateDoc(doc(db, "students", student.id), {
        enrollments: studentData.enrollments,
        firstName: studentData.firstName,
        lastName: studentData.lastName,
        phone: studentData.phone,
        disabled: studentData.disabled,
        startDate: studentData.startDate || null,  
        endDate: studentData.endDate || null    
      });

      const groupUpdatePromises = studentData.enrollments.map((enrollment) =>
        updateDoc(doc(db, "groups", enrollment.groupId), {
          studentIds: arrayUnion(student.id)
        })
      );
      await Promise.all(groupUpdatePromises);

      setStudents((prev) => prev.map((s) => (s.id === student.id ? { ...s, ...studentData } : s)));

      setSuccess(t("Changes saved successfully"));
      onClose();
    } catch (error) {
      console.error("Erreur lors de la sauvegarde :", error);
      setError(t("An error occurred while saving changes"));
    }
    setError("");
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          overflow: "hidden",
          borderRadius: isMobile ? 0 : 3,
          direction: "rtl", // <<< RTL
          boxShadow: isMobile ? "none" : "0 20px 60px rgba(15, 23, 42, 0.18)",
          border: isMobile ? "none" : "1px solid rgba(15, 23, 42, 0.06)",
        }
      }}
    >
      {/* App-style header (RTL) */}
      <AppBar position="relative" color="default" elevation={0} sx={{ borderBottom: 1, borderColor: "divider", direction: "rtl" }}>
        <Toolbar sx={{ gap: 1 }}>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 700, textAlign: "center" }}>
           {t("Edit student information")}
          </Typography>
          <IconButton onClick={onClose} edge="end">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <DialogContent
        sx={{
          p: { xs: 2, md: 3 },
          bgcolor: "background.default",
          direction: "rtl",
          "& .MuiOutlinedInput-root": { borderRadius: 2 },
          "& .MuiInputBase-root": { borderRadius: 2 },
          "& .MuiButton-containedPrimary": {
            borderRadius: 2,
            bgcolor: "primary.main",
            "&:hover": { bgcolor: "primary.dark" },
          },
          "& .MuiButton-outlinedPrimary": { borderRadius: 2 },
        }}
      >
        {/* Status + Devices side-by-side */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {/* حالة الحساب */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }} dir="rtl">
              {/* Header: Status + Switch */}
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                 {t("Account status")}
                </Typography>

                <FormControlLabel
                  control={
                    <Switch
                      checked={!studentData.disabled}
                      onChange={(e) =>
                        setStudentData({ ...studentData, disabled: !e.target.checked })
                      }
                      color="success"
                    />
                  }
                  label={studentData.disabled ? t("Activate account") : t("Disable account")}
                  sx={{ ml: 0 }}
                />
              </Stack>

              {/* Divider */}
              <Divider sx={{ my: 2 }} />

              {/* Dates Section */}
              <Grid container spacing={2}>
                {/* Start Date */}
                <Grid item xs={12} sm={6}>
                  <Stack spacing={0.75}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <CalendarMonthRoundedIcon fontSize="small" />
                      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                       {t("Start date")}
                      </Typography>
                    </Stack>

                    <TextField
                      type="date"
                      size="small"
                      fullWidth
                      value={studentData.startDate || ''}
                      onChange={(e) =>
                        setStudentData({ ...studentData, startDate: e.target.value })
                      }
                      InputLabelProps={{ shrink: true }}
                      helperText={t("Select the subscription start date")}
                    />
                  </Stack>
                </Grid>

                {/* End Date */}
                <Grid item xs={12} sm={6}>
                  <Stack spacing={0.75}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <EventAvailableRoundedIcon fontSize="small" />
                      <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                        {t("تاريخ النهاية")}
                      </Typography>
                    </Stack>

                    <TextField
                      type="date"
                      size="small"
                      fullWidth
                      value={studentData.endDate || ''}
                      onChange={(e) =>
                        setStudentData({ ...studentData, endDate: e.target.value })
                      }
                      InputLabelProps={{ shrink: true }}
                      helperText={t("حدد تاريخ نهاية الاشتراك")}
                    />
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* أجهزة المستخدم */}
          <Grid item xs={12} md={6}>
            <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                 {t("Student devices (max 2)")}
                </Typography>
              </Stack>

              {!studentData?.uid ? (
                <Alert severity="warning">{t("No UID for this student.")}</Alert>
              ) : loadingDevices ? (
                <Typography variant="body2">{t("Loading...")}</Typography>
              ) : (devicesDoc?.devices?.length ?? 0) === 0 ? (
                <Alert severity="info">{t("No devices registered.")}</Alert>
              ) : (
                <Stack direction="row" gap={1} flexWrap="wrap">
                  {devicesDoc.devices.map((id) => (
                    <Chip
                      key={id}
                      label={formatDeviceLabel(id)}
                      onDelete={() => handleRemoveDevice(id)}
                      deleteIcon={<DeleteIcon />}
                      variant="outlined"
                      sx={{
                        padding: '10px',
                        maxWidth: "100%",
                        whiteSpace: "normal",   // allow wrapping
                        height: "auto",         // adjust height to content
                        "& .MuiChip-label": {
                          display: "block",     // force label to use full width
                          whiteSpace: "normal",
                          textAlign: "right"    // keep RTL alignment
                        }
                      }}
                    />
                  ))}
                </Stack>

              )}
            </Paper>
          </Grid>
        </Grid>

        {/* Student basic info */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, mt: 7 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2 }}>
           {t("Student information")}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("First name")}
                fullWidth
                value={studentData.firstName}
                onChange={(e) => setStudentData({ ...studentData, firstName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label={t("Last name")}
                fullWidth
                value={studentData.lastName}
                onChange={(e) => setStudentData({ ...studentData, lastName: e.target.value })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField label={t("Email")} fullWidth value={studentData.email} disabled />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label={t("Phone")} fullWidth value={studentData.phone} disabled />
            </Grid>
          </Grid>
        </Paper>

        {/* Enrollments */}
        <Paper variant="outlined" sx={{ p: 2, direction: "rtl" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {t("Enrollments")}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowAddGroupInputs((prev) => !prev)}
            >
              {t("Add student to a group (class)")}
            </Button>
          </Stack>

          {studentData.enrollments?.length ? (
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2}}>
              {studentData.enrollments.map((enrollment) => (
                <Chip
                  key={enrollment.groupId}
                  label={`${t("Group")}: ${enrollment.groupName}`}
                  onDelete={() => handleRemoveGroup(enrollment.groupId)}
                  deleteIcon={<DeleteIcon />}
                  variant="outlined"
                  sx={{
                    padding: "10px",
                    fontSize: "16px",           // increase text size
                    fontWeight: "500",          // make it bolder for readability
                    "& .MuiChip-label": {
                      fontSize: "16px",         // ensure label text size increases too
                    },
                  }}
                />
              ))}
            </Stack>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t("No enrollments yet")}
            </Alert>
          )}

          <Divider sx={{ my: 2 }} />

          <Collapse in={showAddGroupInputs} unmountOnExit>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="level-label">{t("Level")}</InputLabel>
                  <Select
                    labelId="level-label"
                    label={t("Level")}
                    value={selectedLevel}
                    onChange={(e) => setSelectedLevel(e.target.value)}
                  >
                    <MenuItem value="">{t("Select level")}</MenuItem>
                    {LEVELS.map((level) => (
                      <MenuItem key={level} value={level}>
                        {level}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth disabled={!filteredSubjects.length}>
                  <InputLabel id="subject-label">{t("Subject")}</InputLabel>
                  <Select
                    labelId="subject-label"
                    label={t("Subject")}
                    value={selectedSubject}
                    onChange={(e) => setSelectedSubject(e.target.value)}
                  >
                    <MenuItem value="">{t("Select subject")}</MenuItem>
                    {filteredSubjects.map((subject) => (
                      <MenuItem key={subject.id} value={subject.id}>
                        {subject.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth disabled={!filteredTeachers.length}>
                  <InputLabel id="teacher-label">{t("Teacher")}</InputLabel>
                  <Select
                    labelId="teacher-label"
                    label={t("Teacher")}
                    value={selectedTeacher}
                    onChange={(e) => setSelectedTeacher(e.target.value)}
                  >
                    <MenuItem value="">{t("Select teacher")}</MenuItem>
                    {filteredTeachers.map((teacher) => (
                      <MenuItem key={teacher.id} value={teacher.id}>
                        {`${teacher.firstName} ${teacher.lastName}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth disabled={!filteredGroups.length}>
                  <InputLabel id="group-label">{t("Group (class)")}</InputLabel>
                  <Select
                    labelId="group-label"
                    label={t("Group (class)")}
                    value={selectedGroup}
                    onChange={(e) => setSelectedGroup(e.target.value)}
                  >
                    <MenuItem value="">{t("Select group")}</MenuItem>
                    {filteredGroups.map((group) => (
                      <MenuItem key={group.id} value={group.id}>
                        {group.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Button
                  fullWidth
                  variant="outlined"
                  color="success"
                  onClick={handleAddGroup}
                  disabled={!selectedLevel || !selectedSubject || !selectedTeacher || !selectedGroup}
                  startIcon={<AddIcon />}
                >
                  {t("Add student to group")}
                </Button>
              </Grid>
            </Grid>
          </Collapse>
        </Paper>
      </DialogContent>

      <DialogActions sx={{ p: { xs: 2, md: 3 }, direction: "rtl" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
          <Button color="error" onClick={onClose} sx={{ border: '1px solid' }}>
            {t("Cancel")}
          </Button>

          <Button
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={!isModified()}                 
          >
            {t("Save information")}
          </Button>

          <Button color="primary" onClick={() => handlePrint(studentData)} sx={{border: '1px solid'}}>
            {t("Print receipt")}
          </Button>
        </Box>
      </DialogActions>

      {/* Snackbars */}
      <Snackbar
        open={Boolean(success)}
        autoHideDuration={3000}
        onClose={() => setSuccess("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="success" onClose={() => setSuccess("")} sx={{ width: "100%" }}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={4000}
        onClose={() => setError("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setError("")} sx={{ width: "100%" }}>
          {error}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ModifyStudentModal;
